// Mock interview expiry (JD-QA-03). The backend decides the deadline and sends it as
// `expiresAt` along with `serverTime`; the page only counts down to it, so a wrong clock on
// the candidate's machine does not matter.

export const INTERVIEW_SCHEDULED = "INTERVIEW_SCHEDULED";
export const INTERVIEW_EXPIRED = "INTERVIEW_EXPIRED";

// Browsers run a setTimeout longer than this immediately, so long waits are re-armed.
export const MAX_TIMEOUT_MS = 2147483647;

/** Milliseconds to add to the local clock to get the server's clock. */
export function clockOffset(serverTime, clientNow = Date.now()) {
  const server = Date.parse(serverTime);
  return Number.isNaN(server) ? 0 : server - clientNow;
}

export function isPastExpiry(expiresAt, offset = 0, clientNow = Date.now()) {
  const deadline = Date.parse(expiresAt);
  return !Number.isNaN(deadline) && clientNow + offset >= deadline;
}

/** The state to show right now: a scheduled interview past its deadline is expired. */
export function liveInterviewState(state, expiresAt, offset = 0, clientNow = Date.now()) {
  return state === INTERVIEW_SCHEDULED && isPastExpiry(expiresAt, offset, clientNow)
    ? INTERVIEW_EXPIRED
    : state;
}

/** Milliseconds until the next scheduled interview in `items` expires, or null if none will. */
export function nextExpiryDelay(items, offset = 0, clientNow = Date.now()) {
  let next = null;
  (items || []).forEach((item) => {
    if (item?.interviewState !== INTERVIEW_SCHEDULED) return;
    const deadline = Date.parse(item?.expiresAt);
    if (Number.isNaN(deadline)) return;
    const delay = deadline - (clientNow + offset);
    if (delay > 0 && (next === null || delay < next)) next = delay;
  });
  return next;
}

/** The browser's IANA time zone, sent when scheduling so the link expires at midnight there. */
export function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch (e) {
    return undefined;
  }
}

// The last second the link works, i.e. 23:59:59 on the expiry day in the candidate's zone.
function lastValidMoment(expiresAt) {
  const deadline = Date.parse(expiresAt);
  return Number.isNaN(deadline) ? null : new Date(deadline - 1000);
}

/**
 * "6 Jun 2026, 11:59 pm" in the viewer's zone. Someone in another zone sees the same moment in
 * their own time. Falls back to the picked day when the backend sent no expiresAt.
 */
export function formatDeadline(expiresAt, expiryDate, timeZone) {
  const last = lastValidMoment(expiresAt);
  if (!last) return formatExpiryDay(expiryDate);
  return last.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  });
}

/** The day of the deadline in the viewer's zone, e.g. "6 Jun 2026". */
export function formatDeadlineDay(expiresAt, expiryDate, timeZone) {
  const last = lastValidMoment(expiresAt);
  if (!last) return formatExpiryDay(expiryDate);
  return last.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone,
  });
}

/**
 * The expiry date as the candidate picked it. It is stored as that calendar date in UTC, so
 * format it in UTC; formatting in the browser's zone can show the next day.
 */
export function formatExpiryDay(expiryDate) {
  const date = new Date(expiryDate);
  if (!expiryDate || Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}
